import React, { useEffect, useMemo } from 'react';
import { Browser } from '@capacitor/browser';
import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Image,
  Link,
  Skeleton,
  Text,
  useColorModeValue,
  useTheme,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ENERGY_MASTERS_TENANT_ID } from 'clipsal-cortex-utils/src/constants/common-constants';

import amberLogoImg from '../../../assets/images/amber_logo.svg';
import sapnLogoDark from '../../../assets/images/sapn_logo_dark.svg';
import sapnLogo from '../../../assets/images/sapn_logo.svg';
import { useSiteHasSwitchingMeter } from '../../../common/hooks/use-site-has-switching-meter';
import { selectSite } from '../../site/siteSlice';
import { useImportLimitedDevices } from '../profileApi';
import { ProfilePageBase } from '../ProfilePageBase';
import { SAPN_ENERGY_MASTERS_WEBSITE_URL } from './smart-save-helpers';
import { useSmartSaveStatus } from './use-smartsave-status';

export const SmartSaveSettings = () => {
  const { smartSaveStatus, isLoading } = useSmartSaveStatus();
  const borderColor = useColorModeValue('gray.300', 'black');
  const background = useColorModeValue('white', 'dusk100.800');
  const navigate = useNavigate();
  const { tenant_id: tenantId, site_id: siteId } = useSelector(selectSite);
  const isEnergyMastersSite = tenantId === ENERGY_MASTERS_TENANT_ID;
  const {
    integrations: { amber_b2b: hasAmberControlIntegration },
  } = useSelector(selectSite);
  const siteHasSwitchingMeter = useSiteHasSwitchingMeter();

  useEffect(() => {
    if (!siteHasSwitchingMeter) {
      navigate(`/site/${siteId}/profile`);
    }
  }, [siteHasSwitchingMeter, navigate, siteId]);

  return (
    <ProfilePageBase title={'SmartSave Settings'}>
      <Box pt={4}>
        <Text px={4}>
          SmartSave helps save on bills by optimising your home energy use and through access to partner programs.
        </Text>

        <Skeleton isLoaded={!isLoading}>
          <Flex
            data-testid="smartsave-selected-option"
            borderBottom="1px solid"
            borderColor={borderColor}
            background={background}
            justify={'space-between'}
            align={'center'}
            py={4}
            my={6}
            px={6}
            onClick={() => navigate('optimisation-settings')}
            cursor={'pointer'}
          >
            <Heading fontSize={18}>Optimisations</Heading>
            <Button
              rightIcon={<ChevronRightIcon w={'26px'} h={'26px'} />}
              variant={'ghost'}
              fontWeight={'normal'}
              data-testid="select-smart-save-option"
            >
              {smartSaveStatus}
            </Button>
          </Flex>
        </Skeleton>

        {isEnergyMastersSite && (
          <Text fontSize={'12px'} mb={2} ml={4}>
            PARTNER PROGRAM(S)
          </Text>
        )}
        {isEnergyMastersSite && <SAPNPartnerProgram />}
        {hasAmberControlIntegration && <AmberPartnerProgram />}
      </Box>
    </ProfilePageBase>
  );
};

export const SAPNPartnerProgram = () => {
  const background = useColorModeValue('white', 'dusk100.800');
  const logoSource = useColorModeValue(sapnLogo, sapnLogoDark);
  const navigate = useNavigate();
  const { data: importLimitedDevices, isLoading, isError } = useImportLimitedDevices();

  const optedOutDevicesCopy = useMemo(() => {
    // Error is handled better inside the settings page, can just ignore it here.
    if (isLoading || isError) return '';

    const optedOutDevices = importLimitedDevices.filter((device) => !!device.ignore_until_datetime_utc);

    if (optedOutDevices.length < 1) return 'None';

    return `${optedOutDevices.length} device${optedOutDevices.length > 1 ? 's' : ''}`;
  }, [importLimitedDevices, isLoading, isError]);

  const openEnergyMastersInfoUrl = async () => {
    /* istanbul ignore next -- @preserve */
    await Browser.open({
      url: SAPN_ENERGY_MASTERS_WEBSITE_URL,
    });
  };

  return (
    <Box bg={background}>
      <Box pt={6} px={6}>
        <Center>
          <Center>
            <Box color="red">
              <Image src={logoSource} w={65} h={38} />
            </Box>
            <Text fontWeight={600} ml={2}>
              SA Power Networks
            </Text>
          </Center>
          <Center ml="auto">
            <Box my="auto" w={2} h={2} bg="green.400" borderRadius={'100%'} />
            <Text ml={1}>Active</Text>
          </Center>
        </Center>
        <Text mt={2} fontSize={'14px'}>
          An active connection allows SmartSave to adjust your home's energy via connected devices in response to import
          limit events happening on the grid.
          <Box as="span" ml={1} color={'customBlue.500'}>
            <Link onClick={openEnergyMastersInfoUrl}>Find out more...</Link>
          </Box>
        </Text>
        <Text my={4} fontSize={'14px'}>
          By helping the grid, you are rewarded with a subsidy on your energy bill!
        </Text>
        <Text fontSize={'14px'}>To deactivate this, contact Energy Masters.</Text>
      </Box>

      <Box mt={5} borderTop="1px solid" borderColor="textGrey.500">
        <Box
          data-testid="import-limit-opt-out-link-btn"
          as={'button'}
          onClick={() => navigate('import-limit-settings')}
          w="100%"
          mt={5}
          pb={6}
          px={6}
        >
          <Flex py={1} justify="space-between">
            <Heading size="sm">Devices Opt-out</Heading>
            <Flex align="center">
              <Text mr={2}>{optedOutDevicesCopy}</Text>
              <ChevronRightIcon w={6} h={6} />
            </Flex>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export const AmberPartnerProgram = () => {
  const theme = useTheme();
  const background = useColorModeValue('white', 'dusk100.800');
  const borderColor = useColorModeValue(theme.colors.gray[200], theme.colors.gray[500]);

  return (
    <Box data-testid="amber-partner-program" bg={background} p={6} borderTop={`1px solid ${borderColor}`}>
      <Center>
        <Center>
          <Image src={amberLogoImg} w={65} h={38} />
          <Text fontWeight={600} ml={2}>
            Amber
          </Text>
        </Center>
        <Center ml="auto">
          <Box my="auto" w={2} h={2} bg="green.400" borderRadius={'100%'} />
          <Text ml={1}>Active</Text>
        </Center>
      </Center>
      <Text mt={2} fontSize={'14px'}>
        An active connection allows SmartSave to connect your home battery with Amber's SmartShift™ to make the most of
        wholesale prices.
      </Text>
    </Box>
  );
};
